import {
  FailedTransactionNoticeResponse,
  PaymentsService,
  StudentToOrgTransactionResponse,
} from "client/openapi";
import { DialogContent } from "components/Dialog";
import { Button, ButtonColor, ButtonSize, ButtonFill } from "components/Button";
import moment from "moment";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import { FaClock } from "react-icons/fa6";
import { getTransactionStatusColor } from "pages/manage/students/[id]/PaymentsTab/Transactions/statusHelper";
import { useNavigate } from "react-router-dom";
import TransactionDetailsPopup from "pages/manage/students/[id]/PaymentsTab/Transactions/TransactionMeetingDetails";
import { useState } from "react";
import { Dialog, DialogTrigger } from "components/Dialog";
import { studentToOrgTransactionTypeToHumanReadable } from "../Transactions";

function FailedStudentToOrgTransactions({
  failedStudentToOrgTransactions,
  setFailedStudentToOrgTransactions,
}: {
  failedStudentToOrgTransactions: FailedTransactionNoticeResponse[];
  setFailedStudentToOrgTransactions: (
    failedStudentToOrgTransactions: FailedTransactionNoticeResponse[]
  ) => void;
}) {
  const navigate = useNavigate();
  const [dismissingNoticeId, setDismissingNoticeId] = useState<number | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleMarkAsAddressed = async (noticeId: number) => {
    try {
      setIsLoading(true);
      await PaymentsService.deleteFailedTransactionNotice({ noticeId });
      setFailedStudentToOrgTransactions(
        failedStudentToOrgTransactions.filter(
          (notice) => notice.id !== noticeId
        )
      );
      setDismissingNoticeId(null);
    } catch (error) {
      console.error("Failed to mark transaction as addressed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogContent className="notification-banner-dialog-content">
      <div className="w-full max-h-[50rem] overflow-y-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-3 shadow-sm bg-white">
            <tr className="border-b-2 border-gray-300">
              <th className="text-left p-2 text-sm font-bold w-1/6">Date</th>
              <th className="text-left p-2 text-sm font-bold w-1/5">People</th>
              <th className="text-left p-2 text-sm font-bold w-1/6">Amount</th>
              <th className="text-left p-2 text-sm font-bold w-1/6">Service</th>
              <th className="text-left p-2 text-sm font-bold w-1/6">Type</th>
              <th className="p-2 text-sm font-bold w-1/12 text-center">
                Status
              </th>
              <th className="p-2 text-sm font-bold w-1/12 text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {failedStudentToOrgTransactions.map((notice, index) => {
              const transaction = notice.student_to_org_transaction;
              const date = moment(transaction.timestamp).format("MM/DD/YYYY");
              const people =
                transaction.student.first_name +
                " " +
                transaction.student.last_name;
              const amount = (transaction.amount / 100).toFixed(2);
              const service = transaction.reason || "";
              const type =
                studentToOrgTransactionTypeToHumanReadable.find(
                  (t) => t.value === transaction.student_to_org_transaction_type
                )?.label ?? "Unknown";

              const statusIcon = (() => {
                const statusColor = getTransactionStatusColor(
                  transaction.status
                );
                switch (statusColor) {
                  case "green":
                    return <IoCheckmarkCircle className="mx-auto green-mark" />;
                  case "purple":
                    return (
                      <IoCheckmarkCircle className="mx-auto purple-mark" />
                    );
                  case "orange":
                    return (
                      <FaClock
                        className="mx-auto"
                        style={{ color: "#FFB341", fontSize: "20px" }}
                      />
                    );
                  case "red":
                    return <IoCloseCircle className="mx-auto red-mark" />;
                  default:
                    return (
                      <FaClock
                        className="mx-auto"
                        style={{ color: "lightblue", fontSize: "20px" }}
                      />
                    );
                }
              })();

              const isDismissing = dismissingNoticeId === notice.id;

              return (
                <tr
                  key={index}
                  className="bg-white border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="p-2 text-sm">{date}</td>
                  <td className="p-2 text-sm">{people}</td>
                  <td className="p-2 text-sm">${amount}</td>
                  <td className="p-2 text-sm">{service}</td>
                  <td className="p-2 text-sm">{type}</td>
                  <td className="p-2 text-sm text-center">{statusIcon}</td>
                  <td className="p-2 text-sm text-center">
                    <div className="flex flex-row gap-1 items-center justify-center ">
                      <LazyTransactionDetailsPopup transaction={transaction} />
                      <Button
                        onClick={() =>
                          navigate(
                            `/manage/students/${transaction.student_id}#billing`
                          )
                        }
                        color={ButtonColor.SKYBLUE}
                        size={ButtonSize.EXTRA_SMALL}
                        fill={ButtonFill.HOLLOW}
                      >
                        Student
                      </Button>
                      <Button
                        onClick={() => {
                          if (isDismissing) {
                            handleMarkAsAddressed(notice.id);
                          } else {
                            setDismissingNoticeId(notice.id);
                          }
                        }}
                        color={ButtonColor.GREEN}
                        size={ButtonSize.EXTRA_SMALL}
                        fill={
                          isDismissing ? ButtonFill.DEFAULT : ButtonFill.HOLLOW
                        }
                        disabled={isLoading}
                      >
                        {isDismissing ? "Confirm" : "Dismiss"}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </DialogContent>
  );
}

function LazyTransactionDetailsPopup({
  transaction,
}: {
  transaction: StudentToOrgTransactionResponse;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button
          color={ButtonColor.PURPLE}
          size={ButtonSize.EXTRA_SMALL}
          fill={ButtonFill.HOLLOW}
        >
          Details
        </Button>
      </DialogTrigger>
      {open && <TransactionDetailsPopup transaction={transaction} />}
    </Dialog>
  );
}

export default FailedStudentToOrgTransactions;
